import { Bot, Heart, Leaf, Plane, User } from 'lucide-react'
import { CURRENT_CLIENT } from '@/constants'
import { InitialsAvatar } from '@/components/ui/initials-avatar'
import { useDocumentDarkClass } from '@/hooks/use-document-dark-class'
import { useTenantLogoSrc } from '@/hooks/use-tenant-logo-src'
import {
  resolveChatAgentMarkSrc,
  type ClientMarkConfig,
} from '@/lib/client-mark-src'
import { cn } from '@/lib/utils'

const AVATAR_SHELL =
  'flex size-8 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground'

function getBrandIcon(iconName: string) {
  switch (iconName) {
    case 'Plane':
      return Plane
    case 'Leaf':
      return Leaf
    case 'Heart':
      return Heart
    default:
      return Bot
  }
}

export function ChatUserAvatar({
  mode = 'icon',
  avatarUrl,
  colorSeed,
  alt = 'You',
  className,
}: {
  mode?: 'icon' | 'profile'
  avatarUrl?: string | null
  colorSeed?: string
  alt?: string
  className?: string
}) {
  if (mode === 'profile') {
    return (
      <InitialsAvatar
        src={avatarUrl}
        colorSeed={colorSeed || 'user'}
        alt={alt}
        className={cn('size-8 shrink-0 border-0', className)}
      />
    )
  }

  return (
    <div className={cn(AVATAR_SHELL, className)} aria-hidden>
      <User className="h-4 w-4" />
    </div>
  )
}

export function ChatAgentAvatar({ className }: { className?: string }) {
  const cc = CURRENT_CLIENT as ClientMarkConfig & { brandIcon?: string; name?: string }
  const isDark = useDocumentDarkClass()
  const logoSrc = useTenantLogoSrc({
    logoPath: cc.logoPath,
    logoPathLight: cc.logoPathLight,
    logoPathDark: cc.logoPathDark,
  })
  const markSrc = resolveChatAgentMarkSrc(
    {
      faviconPath: cc.faviconPath,
      faviconStyle: cc.faviconStyle,
      brandIcon: cc.brandIcon,
      theme: cc.theme,
    },
    logoSrc,
    isDark,
  )

  if (markSrc) {
    return (
      <div className={cn(AVATAR_SHELL, 'overflow-hidden', className)} aria-hidden>
        <img src={markSrc} alt="" className="h-4 w-4 object-contain" />
      </div>
    )
  }

  const IconComponent = getBrandIcon(cc.brandIcon || 'Bot')
  return (
    <div className={cn(AVATAR_SHELL, className)} aria-hidden>
      <IconComponent className="h-4 w-4" />
    </div>
  )
}
